import React, { useEffect, useRef, useState } from 'react';

// Combobox simple: filtra `productos` mientras se escribe y llama a
// onChange(producto_id | null) cuando el usuario selecciona una opción o
// borra el texto. No depende de ninguna librería externa.
// Si se pasa `onCrear`, cuando lo escrito no coincide con ningún producto
// aparece una fila "+ Crear «texto»" que llama onCrear(texto).
//
// Teclado (sin mouse): ↓/↑ recorren las opciones, Enter confirma la
// resaltada (guardar), Esc cierra el desplegable y descarta lo tipeado
// (cancelar).
export default function AutocompleteProducto({ productos, value, onChange, onCrear, placeholder }) {
  const [texto, setTexto] = useState('');
  const [abierto, setAbierto] = useState(false);
  const [resaltado, setResaltado] = useState(0);
  const contenedorRef = useRef(null);
  const inputRef = useRef(null);
  const listaRef = useRef(null);

  const nombreDeValue = () => {
    const seleccionado = productos.find((p) => String(p.id) === String(value));
    return seleccionado ? seleccionado.nombre : '';
  };

  useEffect(() => {
    setTexto(nombreDeValue());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, productos]);

  useEffect(() => {
    function alClickearFuera(e) {
      if (contenedorRef.current && !contenedorRef.current.contains(e.target)) setAbierto(false);
    }
    document.addEventListener('mousedown', alClickearFuera);
    return () => document.removeEventListener('mousedown', alClickearFuera);
  }, []);

  const q = texto.trim();
  const filtrados = q
    ? productos.filter((p) => p.nombre.toLowerCase().includes(q.toLowerCase())).slice(0, 8)
    : productos.slice(0, 8);
  const hayExacto = q && productos.some((p) => p.nombre.toLowerCase() === q.toLowerCase());
  const mostrarCrear = !!onCrear && q.length > 0 && !hayExacto;

  const opciones = [
    ...filtrados.map((p) => ({ tipo: 'sel', producto: p })),
    ...(mostrarCrear ? [{ tipo: 'crear' }] : [])
  ];

  useEffect(() => { setResaltado(0); }, [texto]);

  useEffect(() => {
    if (!abierto || !listaRef.current) return;
    const el = listaRef.current.children[resaltado];
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [resaltado, abierto]);

  function activar(opcion) {
    if (!opcion) return;
    if (opcion.tipo === 'crear') {
      setAbierto(false);
      onCrear(q);
    } else {
      setTexto(opcion.producto.nombre);
      setAbierto(false);
      onChange(opcion.producto.id);
    }
  }

  function alCambiarTexto(e) {
    const nuevo = e.target.value;
    setTexto(nuevo);
    setAbierto(true);
    if (!nuevo.trim()) onChange(null);
  }

  // Al salir del campo: si se escribió el nombre exacto de un producto pero no
  // se eligió de la lista, resolverlo igual (evita el "escribí el nombre y no
  // agregó nada"). Si no hay match exacto, se deja como está.
  function alSalir() {
    if (value) return;
    const t = texto.trim().toLowerCase();
    if (!t) return;
    const exacto = productos.find((p) => p.nombre.toLowerCase() === t);
    if (exacto) { setTexto(exacto.nombre); onChange(exacto.id); }
  }

  function alTeclear(e) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setAbierto(true);
      setResaltado((i) => Math.min(i + 1, opciones.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setResaltado((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      if (abierto && opciones.length > 0) {
        e.preventDefault();
        activar(opciones[resaltado] || opciones[0]);
      }
    } else if (e.key === 'Escape') {
      if (abierto) {
        e.preventDefault();
        e.stopPropagation();
        setAbierto(false);
        setTexto(nombreDeValue()); // descarta lo tipeado, vuelve al valor actual
      } else {
        inputRef.current?.blur();
      }
    }
  }

  return (
    <div className="relative" ref={contenedorRef}>
      <input
        ref={inputRef}
        value={texto}
        onChange={alCambiarTexto}
        onFocus={() => setAbierto(true)}
        onBlur={alSalir}
        onKeyDown={alTeclear}
        placeholder={placeholder || 'Buscar producto...'}
        className="border rounded-md px-2 py-1.5 w-full text-sm"
        autoComplete="off"
      />
      {abierto && opciones.length > 0 && (
        <ul ref={listaRef} className="absolute z-10 mt-1 w-full bg-white border rounded-md shadow-lg max-h-48 overflow-y-auto text-sm">
          {filtrados.map((p, idx) => (
            <li
              key={p.id}
              onClick={() => activar({ tipo: 'sel', producto: p })}
              onMouseEnter={() => setResaltado(idx)}
              className={`px-3 py-1.5 cursor-pointer ${resaltado === idx ? 'bg-primary-50' : ''}`}
            >
              {p.nombre}
            </li>
          ))}
          {mostrarCrear && (
            <li
              onClick={() => activar({ tipo: 'crear' })}
              onMouseEnter={() => setResaltado(filtrados.length)}
              className={`px-3 py-1.5 text-primary-600 cursor-pointer ${filtrados.length > 0 ? 'border-t' : ''} ${resaltado === filtrados.length ? 'bg-primary-50' : ''}`}
            >
              + Crear «{q}»
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
